import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import CloseIcon from "@material-ui/icons/Close";
import axios from "axios";
import grey from "@material-ui/core/colors/grey";
import { useHistory } from "react-router-dom";
import Pagination from "../Pagination";
import ProgrssBar from "../ProgressBar";
import ConfirmDialog from "../ConfirmDialog";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    borderRadius: 14,
    boxShadow: "0 10px 24px rgba(15, 23, 42, 0.08)",
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: "#1f2937",
  },
  actionsRow: {
    marginTop: theme.spacing(2),
  },
  refreshButton: {
    backgroundColor: "#00897b",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#00796b",
    },
  },
  tableWrap: {
    maxHeight: 360,
    overflow: "auto",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    backgroundColor: "#fff",
  },
  tableHeadCell: {
    backgroundColor: "#3f51b5",
    color: "#fff",
    fontWeight: 600,
    whiteSpace: "nowrap",
    fontSize: "0.83rem",
    letterSpacing: "0.01em",
    paddingTop: 12,
    paddingBottom: 12,
  },
  tableRow: {
    "&:nth-of-type(even)": {
      backgroundColor: "#f8fafc",
    },
    "&:hover": {
      backgroundColor: "#eef4ff",
    },
    "& td": {
      verticalAlign: "top",
      paddingTop: 14,
      paddingBottom: 14,
    },
  },
  tableCheckboxCell: {
    verticalAlign: "top !important",
    paddingTop: "14px !important",
    paddingBottom: "14px !important",
    width: 52,
  },
  emptyCell: {
    padding: theme.spacing(4, 2),
    color: "#6b7280",
  },
  subtleText: {
    color: "#475569",
  },
  helperBox: {
    padding: theme.spacing(1.5),
    background: "#f8fafc",
    borderRadius: 10,
    border: "1px solid #e5e7eb",
  },
  carrierStatusGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: theme.spacing(1.25),
  },
  carrierStatusCard: {
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 12,
    border: "1px solid #dbe4f0",
    background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
  },
  carrierStatusCardMuted: {
    background: "#f8fafc",
    border: "1px dashed #d7dee8",
    boxShadow: "none",
  },
  carrierStatusName: {
    fontWeight: 600,
    color: "#1f2937",
    marginBottom: theme.spacing(0.25),
  },
  carrierStatusValue: {
    color: "#475569",
    fontSize: "0.92rem",
    lineHeight: 1.45,
  },
  denseActions: {
    display: "flex",
    gap: theme.spacing(1.5),
    alignItems: "center",
    flexWrap: "wrap",
  },
  denseActionsNoWrap: {
    display: "flex",
    gap: theme.spacing(1.5),
    alignItems: "center",
    flexWrap: "nowrap",
    whiteSpace: "nowrap",
  },
  configsToolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  configsToolbarActions: {
    display: "flex",
    gap: theme.spacing(1.25),
    alignItems: "center",
    flexWrap: "wrap",
    "& .MuiButton-root": {
      minHeight: 40,
      padding: theme.spacing(0.85, 1.8),
      borderRadius: 12,
      fontWeight: 700,
      letterSpacing: "0.01em",
      textTransform: "none",
      boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
    },
  },
  configsTableUsageBox: {
    minWidth: 132,
    maxWidth: 176,
    padding: theme.spacing(0.9, 1),
    borderRadius: 12,
    border: "1px solid #dbe4f0",
    background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
  },
  configsTableUsageTop: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.35),
    flexWrap: "wrap",
  },
  configsTableUsageStat: {
    color: "#475569",
    fontSize: "0.76rem",
    lineHeight: 1.4,
  },
  configsTableUsageSharedLine: {
    color: "#334155",
    fontSize: "0.83rem",
    lineHeight: 1.45,
    whiteSpace: "nowrap",
  },
  configsTableUsageSharedRemaining: {
    fontWeight: 600,
  },
  configsTableUsageRemainingHealthy: {
    color: "#0f766e",
  },
  configsTableUsageRemainingLow: {
    color: "#b45309",
  },
  configsTableUsageRemainingEmpty: {
    color: "#dc2626",
  },
  configsTableCarrierBox: {
    minWidth: 110,
    maxWidth: 155,
    padding: theme.spacing(0.75, 0.9),
    borderRadius: 12,
    background: "#f8fafc",
    border: "1px solid #e5e7eb",
  },
  configsTableCarrierPrimary: {
    fontWeight: 600,
    color: "#1f2937",
    lineHeight: 1.2,
    marginBottom: theme.spacing(0.35),
  },
  configsTableCarrierSecondary: {
    color: "#475569",
    fontSize: "0.78rem",
    lineHeight: 1.35,
    wordBreak: "break-word",
  },
  configsTableMuted: {
    color: "#64748b",
    fontSize: "0.82rem",
    lineHeight: 1.35,
  },
  configsTablePill: {
    display: "inline-flex",
    alignItems: "center",
    padding: theme.spacing(0.45, 1),
    borderRadius: 999,
    background: "#eef2ff",
    color: "#3f51b5",
    fontSize: "0.78rem",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  configsEnabledBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: theme.spacing(0.45, 1),
    borderRadius: 999,
    background: "#ecfdf5",
    color: "#047857",
    fontSize: "0.78rem",
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  configsDisabledBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: theme.spacing(0.45, 1),
    borderRadius: 999,
    background: "#f8fafc",
    color: "#64748b",
    fontSize: "0.78rem",
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  configsTableActionCell: {
    minWidth: 120,
  },
  configsTableActionLinks: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
    whiteSpace: "nowrap",
  },
  configsActionButton: {
    minWidth: 0,
    padding: theme.spacing(0.55, 1.2),
    textTransform: "none",
    fontWeight: 700,
    borderRadius: 10,
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
    border: "1px solid transparent",
  },
  configsEditButton: {
    color: "#3f51b5",
    background: "#eef2ff",
    borderColor: "#c7d2fe",
    "&:hover": {
      background: "#e0e7ff",
      borderColor: "#a5b4fc",
    },
  },
  configsDeleteButton: {
    color: "#e11d48",
    background: "#fff1f2",
    borderColor: "#fecdd3",
    "&:hover": {
      background: "#ffe4e6",
      borderColor: "#fda4af",
    },
  },
  formIntro: {
    color: "#475569",
    marginBottom: theme.spacing(1.5),
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  compactSwitch: {
    marginRight: 0,
  },
  usageSummaryLine: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  usageMeta: {
    color: "#475569",
    fontSize: "0.8rem",
  },
  usageCompactPrimary: {
    fontWeight: 600,
    color: "#1f2937",
    lineHeight: 1.2,
    marginBottom: theme.spacing(0.25),
  },
  usageCompactSecondary: {
    color: "#64748b",
    fontSize: "0.78rem",
    lineHeight: 1.35,
  },
  usageCompactMetricLine: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    flexWrap: "wrap",
    marginTop: theme.spacing(0.2),
  },
  usageCompactMetric: {
    display: "inline-flex",
    alignItems: "baseline",
    gap: theme.spacing(0.35),
    fontSize: "0.78rem",
    lineHeight: 1.35,
  },
  usageCompactMetricTracked: {
    color: "#475569",
  },
  usageCompactMetricActive: {
    color: "#0f766e",
  },
  usageCompactMetricExhausted: {
    color: "#dc2626",
  },
  usageCompactMetricValue: {
    fontWeight: 700,
    color: "inherit",
  },
  usageCompactMetricLabel: {
    color: "inherit",
  },
  usageCompactButton: {
    minWidth: 0,
    padding: theme.spacing(0.25, 1),
    textTransform: "none",
    whiteSpace: "nowrap",
    marginTop: theme.spacing(0.75),
  },
  usageDialogSection: {
    marginBottom: theme.spacing(2.5),
  },
  usageDialogHeader: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.35),
  },
  usageDialogTitleBar: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(2),
  },
  usageDialogTitleWrap: {
    flex: 1,
    minWidth: 0,
  },
  usageDialogCloseButton: {
    marginTop: theme.spacing(-0.5),
    color: "#64748b",
    border: "1px solid #dbe4f0",
    backgroundColor: "#ffffff",
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
    "&:hover": {
      backgroundColor: "#f8fafc",
      color: "#0f172a",
    },
  },
  usageDialogKicker: {
    color: "#64748b",
    fontSize: "0.76rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  usageDialogTitle: {
    color: "#111827",
    fontWeight: 700,
    lineHeight: 1.2,
  },
  usageDialogSubtitle: {
    color: "#475569",
    fontSize: "0.92rem",
  },
  usageDialogOverview: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: theme.spacing(1.25),
    marginBottom: theme.spacing(1.25),
  },
  usageDialogOverviewCard: {
    borderRadius: 16,
    padding: theme.spacing(1.35, 1.5),
    border: "1px solid #dbe4f0",
    background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.05)",
  },
  usageDialogOverviewLabel: {
    color: "#64748b",
    fontSize: "0.78rem",
    marginBottom: theme.spacing(0.5),
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  usageDialogOverviewValue: {
    color: "#1f2937",
    fontWeight: 700,
    fontSize: "1.05rem",
    lineHeight: 1.2,
  },
  usageDialogOverviewHint: {
    color: "#64748b",
    fontSize: "0.78rem",
    marginTop: theme.spacing(0.45),
    lineHeight: 1.35,
  },
  usageDialogCarrierCard: {
    borderRadius: 16,
    padding: theme.spacing(1.25, 1.5),
    border: "1px solid #dbe4f0",
    background: "#fff",
    boxShadow: "0 6px 16px rgba(15, 23, 42, 0.05)",
  },
  usageMatrixSection: {
    marginBottom: theme.spacing(2.5),
  },
  usageMatrixTabsWrap: {
    marginBottom: theme.spacing(1),
    borderBottom: "1px solid #dbe4f0",
  },
  usageMatrixTabs: {
    minHeight: 40,
  },
  usageMatrixTab: {
    minWidth: 88,
    minHeight: 40,
    padding: theme.spacing(0.5, 1),
    fontSize: "0.82rem",
  },
  usageMatrixSectionTitle: {
    fontWeight: 700,
    color: "#1f2937",
    marginBottom: theme.spacing(1),
  },
  usageMatrixWrap: {
    border: "1px solid #dbe4f0",
    borderRadius: 14,
    overflow: "hidden",
    background: "#fff",
    boxShadow: "0 4px 14px rgba(15, 23, 42, 0.05)",
  },
  usageMatrixTable: {
    "& td, & th": {
      paddingTop: 10,
      paddingBottom: 10,
    },
  },
  usageMatrixHeadCell: {
    backgroundColor: "#eff6ff",
    color: "#1e3a8a",
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  usageMatrixStickyCell: {
    position: "sticky",
    left: 0,
    background: "#fff",
    zIndex: 2,
  },
  usageMatrixStickyCellAlt: {
    position: "sticky",
    left: 0,
    background: "#f8fafc",
    zIndex: 2,
  },
  usageMatrixPortCell: {
    minWidth: 68,
    fontWeight: 600,
    color: "#1f2937",
  },
  usageMatrixStatusCell: {
    minWidth: 116,
    verticalAlign: "top",
  },
  usageMatrixStatusBox: {
    borderRadius: 10,
    padding: theme.spacing(0.7, 0.9),
    border: "1px solid #dbe4f0",
    background: "#fff",
  },
  usageMatrixStatusPrimary: {
    fontWeight: 700,
    color: "#1f2937",
    lineHeight: 1.2,
  },
  usageMatrixStatusSecondary: {
    color: "#64748b",
    fontSize: "0.76rem",
    lineHeight: 1.35,
    marginTop: theme.spacing(0.35),
  },
  usageMatrixStatusAvailable: {
    background: "#eefbf5",
    borderColor: "#9ed8b4",
  },
  usageMatrixStatusLow: {
    background: "#fff7ed",
    borderColor: "#f7c98b",
  },
  usageMatrixStatusExhausted: {
    background: "#fff1f2",
    borderColor: "#f5b6bf",
  },
  usageMatrixStatusUnused: {
    background: "#f8fafc",
    borderColor: "#dbe4f0",
  },
  usageMatrixStatusMissing: {
    background: "#f3f4f6",
    borderColor: "#e5e7eb",
    opacity: 0.88,
  },
  usageDialogCarrierMeta: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  usageDialogCarrierMetaItem: {
    borderRadius: 10,
    padding: theme.spacing(1, 1.25),
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
  },
  usageDialogCarrierMetaLabel: {
    color: "#64748b",
    fontSize: "0.74rem",
    marginBottom: theme.spacing(0.35),
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  usageDialogCarrierMetaValue: {
    color: "#1f2937",
    fontWeight: 600,
  },
  usageDialogSubTitle: {
    fontWeight: 600,
    color: "#334155",
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(0.5),
  },
  usagePortGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(8, minmax(0, 1fr))",
    gap: theme.spacing(1),
  },
  usagePortTile: {
    borderRadius: 12,
    border: "1px solid #dbe4f0",
    padding: theme.spacing(1),
    minHeight: 78,
    background: "#fff",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.45)",
  },
  usagePortTileAvailable: {
    background: "#eefbf5",
    borderColor: "#9ed8b4",
  },
  usagePortTileExhausted: {
    background: "#fff1f2",
    borderColor: "#f5b6bf",
  },
  usagePortTileUnused: {
    background: "#f8fafc",
    borderColor: "#dbe4f0",
  },
  usagePortTileMissing: {
    background: "#f3f4f6",
    borderColor: "#e5e7eb",
    opacity: 0.8,
  },
  usagePortLabel: {
    fontWeight: 600,
    color: "#1f2937",
    lineHeight: 1.2,
  },
  usagePortValue: {
    color: "#475569",
    fontSize: "0.78rem",
    marginTop: theme.spacing(0.5),
    lineHeight: 1.35,
  },
  usagePortStatus: {
    marginTop: theme.spacing(0.4),
    fontSize: "0.7rem",
    fontWeight: 600,
    letterSpacing: "0.03em",
    textTransform: "uppercase",
    color: "#64748b",
  },
}));

const API = process.env.REACT_APP_API_URL;

const carrierOptions = [
  { value: "ALL", label: "All" },
  { value: "VZW", label: "Verizon" },
  { value: "ATT", label: "AT&T" },
  { value: "TMO", label: "TMobile" },
  { value: "SPRINT", label: "Sprint" },
  { value: "ALLOTHER", label: "ALLOTHER" },
];

const filterCarrierOptions = carrierOptions.filter((carrier) => carrier.value !== "ALL");

const carrierLabelMap = carrierOptions.reduce((map, item) => {
  map[item.value] = item.label;
  return map;
}, {});

const singleSubPortBoxes = ["8012", "8013", "8019"];
const allSubPorts = ["1", "2", "3", "4", "5", "6", "7", "8"];

const getAllowedSubPorts = (box) =>
  singleSubPortBoxes.includes(String(box || "")) ? ["1"] : allSubPorts;

const getScopedAllowedSubPorts = (boxes = []) => {
  const selectedBoxes = (Array.isArray(boxes) ? boxes : [])
    .map((box) => String(box || "").trim())
    .filter(Boolean);
  if (!selectedBoxes.length) return allSubPorts;
  return selectedBoxes.every((box) => singleSubPortBoxes.includes(box))
    ? ["1"]
    : allSubPorts;
};

const individualCarriers = carrierOptions
  .filter((carrier) => carrier.value !== "ALL")
  .map((carrier) => carrier.value);

const expandCarriersForDisplay = (carriers = []) => {
  if (!Array.isArray(carriers) || !carriers.length) return [];
  return carriers.includes("ALL") ? individualCarriers : carriers;
};

const getRemainingToneClass = (remaining, limit, classes) => {
  const rem = Number(remaining || 0);
  const lim = Number(limit || 0);
  if (rem <= 0) return classes.configsTableUsageRemainingEmpty;
  if (lim > 0 && rem <= Math.max(1, Math.ceil(lim * 0.25))) {
    return classes.configsTableUsageRemainingLow;
  }
  return classes.configsTableUsageRemainingHealthy;
};

const defaultGlobalConfig = {
  enabled: true,
  mode: "skip",
  limit: 3,
  carriers: ["ALL"],
  cacheEnabled: false,
  cacheTtlMs: 30000,
  safeNumberMode: "pool",
  safeNumbersText: "",
  safeNumberMappings: [],
  fallback: "backwash",
};

const defaultScopedForm = {
  scope: "box",
  boxes: [],
  subPorts: [],
  portsText: "",
  countMode: "per_port",
  enabled: true,
  mode: "skip",
  limit: 3,
  carriers: [],
  safeNumberMode: "pool",
  safeNumbersText: "",
  safeNumberMapText: "",
  fallback: "backwash",
};

const defaultFilters = {
  scope: "",
  box: "",
  subPort: "",
  port: "",
};

const defaultEventFilters = {
  box: "",
  subPort: "",
  port: "",
  carrier: "",
  action: "",
};

const defaultSummaryFilters = {
  box: "",
  subPort: "",
  port: "",
  carrier: "",
  action: "",
};

const normalizePhone = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length === 10) return `1${digits}`;
  return digits;
};

const isValidSafeNumber = (value) => /^1\d{10}$/.test(String(value || ""));

const normalizeUniqueUpperList = (list) =>
  [...new Set((Array.isArray(list) ? list : []).map((item) => String(item).trim().toUpperCase()).filter(Boolean))];

const normalizeCarrierSelection = (selection) => {
  const next = Array.isArray(selection) ? selection : [selection];
  const withoutAll = next.filter((value) => value !== "ALL");
  if (withoutAll.length === individualCarriers.length) {
    return ["ALL"];
  }
  if (next.includes("ALL")) {
    return ["ALL"];
  }
  return withoutAll;
};

const normalizeUniquePhones = (list) =>
  [...new Set((Array.isArray(list) ? list : []).map(normalizePhone).filter(Boolean))];

const parseDelimitedList = (value) =>
  String(value || "")
    .split(/[,\n|]/)
    .map((item) => item.trim())
    .filter(Boolean);

const monthNameToNumber = (value) => {
  const months = {
    jan: 1,
    feb: 2,
    mar: 3,
    apr: 4,
    may: 5,
    jun: 6,
    jul: 7,
    aug: 8,
    sep: 9,
    oct: 10,
    nov: 11,
    dec: 12,
  };
  return months[String(value || "").slice(0, 3).toLowerCase()] || null;
};

const expandPortToken = (token) => {
  const clean = String(token || "").trim();
  if (!clean) return [];
  if (/^\d+$/.test(clean)) return [String(Number(clean))];
  const excelDateLikeMatch = clean.match(/^(\d{1,2})[-/\s]([A-Za-z]{3,9})$/);
  if (excelDateLikeMatch) {
    const start = Number(excelDateLikeMatch[1]);
    const monthNumber = monthNameToNumber(excelDateLikeMatch[2]);
    if (Number.isFinite(start) && monthNumber) {
      const low = Math.min(start, monthNumber);
      const high = Math.max(start, monthNumber);
      const ports = [];
      for (let value = low; value <= high; value += 1) {
        ports.push(String(value));
      }
      return ports;
    }
  }
  const rangeMatch = clean.match(/^(\d+)\s*-\s*(\d+)$/);
  if (!rangeMatch) return [];
  const start = Number(rangeMatch[1]);
  const end = Number(rangeMatch[2]);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return [];
  const low = Math.min(start, end);
  const high = Math.max(start, end);
  const ports = [];
  for (let value = low; value <= high; value += 1) {
    ports.push(String(value));
  }
  return ports;
};

const expandPortsInput = (value) => {
  const tokens = parseDelimitedList(value);
  const output = [];
  tokens.forEach((token) => {
    output.push(...expandPortToken(token));
  });
  return [...new Set(output)].sort((a, b) => Number(a) - Number(b));
};

const parseSequentialMappings = (ports, text) => {
  const lines = String(text || "")
    .split(/\r?\n/)
    .map((line) => normalizePhone(line))
    .filter(Boolean);
  return {
    numbers: lines,
    mappings: ports.map((port, idx) => ({
      port,
      phone: lines[idx] || "",
    })),
  };
};

const normalizeConfigFromApi = (config) => ({
  enabled: config?.enabled !== false,
  mode: config?.mode || "skip",
  limit: Number(config?.limit ?? 3),
  carriers: Array.isArray(config?.carriers) ? config.carriers : ["ALL"],
  cacheEnabled: config?.cacheEnabled === true,
  cacheTtlMs: config?.cacheTtlMs || 30000,
  usageByCarrier: Array.isArray(config?.usageByCarrier) ? config.usageByCarrier : [],
  safeNumberMode: config?.safeNumberMode || "pool",
  safeNumbersText: Array.isArray(config?.safeNumbers)
    ? config.safeNumbers.join("\n")
    : "",
  safeNumberMappings: Array.isArray(config?.safeNumberMappings)
    ? config.safeNumberMappings
    : [],
  fallback: config?.fallback || "backwash",
});

const buildScopedFormFromConfig = (config) => ({
  scope: config?.scope || "box",
  boxes: config?.box ? [String(config.box)] : [],
  subPorts: config?.subPort ? [String(config.subPort)] : [],
  portsText:
    config?.port !== undefined && config?.port !== null && config?.port !== ""
      ? String(config.port)
      : Array.isArray(config?.ports)
      ? config.ports.join(",")
      : "",
  countMode:
    config?.scope === "box" || config?.scope === "subport"
      ? config?.countMode || "shared"
      : "per_port",
  enabled: config?.enabled !== false,
  mode: config?.mode || "skip",
  limit: Number(config?.limit ?? 3),
  carriers: Array.isArray(config?.carriers) ? config.carriers : [],
  safeNumberMode: config?.safeNumberMode || "pool",
  safeNumbersText: Array.isArray(config?.safeNumbers)
    ? config.safeNumbers.join("\n")
    : "",
  safeNumberMapText:
    config?.safeNumberMode === "mapping" && Array.isArray(config?.safeNumbers)
      ? config.safeNumbers.join("\n")
      : Array.isArray(config?.safeNumberMappings)
      ? config.safeNumberMappings.map((item) => item.phone).join("\n")
    : "",
  fallback: config?.fallback || "backwash",
});

const parseCsv = (text) => {
  const lines = String(text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (!lines.length) return { headers: [], rows: [] };
  const headers = lines[0].split(",").map((item) => item.trim());
  const rows = lines.slice(1).map((line) => {
    const values = line.split(",").map((item) => item.trim());
    const row = {};
    headers.forEach((header, idx) => {
      row[header] = values[idx] ?? "";
    });
    return row;
  });
  return { headers, rows };
};

const buildSampleCsv = () => {
  const headers =
    "scope,box,subPort,port,enabled,mode,limit,carriers,countMode,safeNumberMode,safeNumbers,safeNumberMappings,fallback";
  const rows = [
    "box,8014,, ,true,skip,3,ATT|VZW,per_port,pool,12125550001|12125550002,,backwash",
    "subport,8014,2, ,true,reroute,5,ATT,shared,pool,12125550003|12125550004,,dontSend",
    "port,8012,1,1,true,reroute,3,VZW,,mapping,12126818191,,backwash",
    "port,8012,1,2,true,reroute,3,VZW,,mapping,12126818192,,backwash",
  ];
  return `${headers}\n${rows.join("\n")}\n`;
};

const csvMappingFields = [
  "scope",
  "box",
  "subPort",
  "ports",
  "port",
  "enabled",
  "mode",
  "limit",
  "carriers",
  "countMode",
  "safeNumberMode",
  "safeNumbers",
  "safeNumberMappings",
  "fallback",
];

const titleCase = (value) =>
  String(value || "")
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (char) => char.toUpperCase())
    .trim();

const humanizeToken = (value) => {
  const text = String(value || "").trim();
  if (!text) return "-";
  const upper = text.toUpperCase();
  if (carrierLabelMap[upper]) return carrierLabelMap[upper];
  if (upper === "DONTSEND") return "DontSend";
  if (upper === "SUBPORT") return "SubPort";
  if (upper === "PER_PORT") return "Per Port";
  if (upper === "ALLOTHER") return "ALLOTHER";
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const renderSafeTargetsSummary = (config) => {
  if (config?.mode !== "reroute") {
    return [];
  }
  if (config?.safeNumberMode === "mapping") {
    if (Array.isArray(config?.safeNumbers) && config.safeNumbers.length) {
      return config.safeNumbers;
    }
    if (Array.isArray(config?.safeNumberMappings)) {
      return config.safeNumberMappings.map((item) => item.phone);
    }
  }
  if (Array.isArray(config?.safeNumbers)) {
    return config.safeNumbers;
  }
  return [];
};

const renderUsageSummary = (config) => {
  const usage = Array.isArray(config?.usageByCarrier) ? config.usageByCarrier : [];
  const carriers = Array.isArray(config?.carriers) ? config.carriers : [];
  if (
    (config?.scope === "box" || config?.scope === "subport") &&
    String(config?.countMode || "").toLowerCase() === "per_port"
  ) {
    return usage.map((item) => ({
      carrier: carrierLabelMap[String(item?.carrier || "").toUpperCase()] || item?.carrier,
      countMode: "per_port",
      portUsages: Array.isArray(item?.portUsages) ? item.portUsages : [],
    }));
  }
  const normalizedUsage = usage
    .filter((item) => item && item.carrier)
    .map((item) => ({
      carrier: String(item.carrier).toUpperCase(),
      used: Number(item.used || 0),
      limit: Number(item.limit || 0),
      remaining: Number(item.remaining || 0),
    }));

  if (carriers.includes("ALL")) {
    const fallbackLimit =
      normalizedUsage.length > 0
        ? Number(normalizedUsage[0].limit || 0)
        : Number(config?.limit || 0);
    return individualCarriers.map((carrier) => {
      const matched = normalizedUsage.find((item) => item.carrier === carrier);
      const used = Number(matched?.used || 0);
      const limit = Number(matched?.limit ?? fallbackLimit);
      return {
        carrier: carrierLabelMap[carrier] || carrier,
        used,
        limit,
        remaining: Math.max(limit - used, 0),
      };
    });
  }

  return normalizedUsage.map((item) => ({
    carrier: carrierLabelMap[item.carrier] || item.carrier,
    used: item.used,
    limit: item.limit,
    remaining: item.remaining,
  }));
};

const summarizePerPortUsage = (config) => {
  const entries = renderUsageSummary(config).filter(
    (item) => item && item.countMode === "per_port"
  );
  if (!entries.length) return null;

  const carrierCount = entries.length;
  const allPortUsages = entries.flatMap((item) =>
    Array.isArray(item.portUsages) ? item.portUsages : []
  );
  const uniquePortKeys = new Set(
    allPortUsages.map((item) => `${item.subPort || ""}::${item.port || ""}`)
  );
  const exhaustedCount = allPortUsages.filter(
    (item) => Number(item.remaining || 0) <= 0
  ).length;
  const activeCount = allPortUsages.filter(
    (item) => Number(item.remaining || 0) > 0
  ).length;

  return {
    carrierCount,
    trackedPortCount: uniquePortKeys.size,
    exhaustedCount,
    activeCount,
  };
};

const summarizeCarrierPortUsage = (entry) => {
  const portUsages = Array.isArray(entry?.portUsages) ? entry.portUsages : [];
  const trackedPortCount = portUsages.length;
  const activeCount = portUsages.filter((item) => Number(item.remaining || 0) > 0).length;
  const exhaustedCount = portUsages.filter((item) => Number(item.remaining || 0) <= 0).length;
  const unusedCount = portUsages.filter((item) => Number(item.used || 0) === 0).length;
  return {
    trackedPortCount,
    activeCount,
    exhaustedCount,
    unusedCount,
  };
};

const buildUsageMatrix = (config, entries) => {
  const carrierEntries = (Array.isArray(entries) ? entries : []).filter(
    (entry) => entry && entry.countMode === "per_port"
  );
  const subPorts =
    config?.scope === "box"
      ? getAllowedSubPorts(config?.box)
      : [String(config?.subPort || "1")];

  return subPorts.map((subPort) => ({
    subPort,
    ports: PORT_DISPLAY_RANGE.map((port) => {
      const carrierStatuses = carrierEntries.map((entry) => {
        const matched = (Array.isArray(entry.portUsages) ? entry.portUsages : []).find(
          (item) =>
            String(item.subPort || config?.subPort || "1") === String(subPort) &&
            String(item.port || "") === String(port)
        );
        if (!matched) {
          return {
            carrier: entry.carrier,
            status: "missing",
            primary: "N/A",
            secondary: "Not In PRC",
          };
        }
        if (Number(matched.remaining || 0) <= 0) {
          return {
            carrier: entry.carrier,
            status: "exhausted",
            primary: "0 left",
            secondary: `${matched.used}/${matched.limit} used`,
          };
        }
        const limit = Number(matched.limit || 0);
        const used = Number(matched.used || 0);
        const remaining = Number(matched.remaining || 0);
        const lowThreshold = Math.max(1, Math.ceil(limit * 0.25));
        if (used > 0 && remaining <= lowThreshold) {
          return {
            carrier: entry.carrier,
            status: "low",
            primary: `${remaining} left`,
            secondary: `${used}/${limit} used`,
          };
        }
        if (used === 0) {
          return {
            carrier: entry.carrier,
            status: "unused",
            primary: `${remaining} left`,
            secondary: `0/${limit} used`,
          };
        }
        return {
          carrier: entry.carrier,
          status: "available",
          primary: `${remaining} left`,
          secondary: `${used}/${limit} used`,
        };
      });

      return {
        port,
        carrierStatuses,
      };
    }),
  }));
};

const renderGlobalUsageSummary = (config) => {
  const usage = Array.isArray(config?.usageByCarrier) ? config.usageByCarrier : [];
  const carriers = Array.isArray(config?.carriers) ? config.carriers : [];
  const normalizedUsage = usage
    .filter((item) => item && item.carrier)
    .map((item) => ({
      carrier: String(item.carrier).toUpperCase(),
      used: Number(item.used || 0),
      limit: Number(item.limit || 0),
      remaining: Number(item.remaining || 0),
    }));
  const restrictedCarriers = carriers.includes("ALL")
    ? [...individualCarriers]
    : carriers
        .map((carrier) => String(carrier || "").toUpperCase())
        .filter((carrier) => individualCarriers.includes(carrier));
  const limit = Number(config?.limit || 0);

  return individualCarriers.map((carrier) => {
    const matched = normalizedUsage.find((item) => item.carrier === carrier);
    const restricted = restrictedCarriers.includes(carrier);
    return {
      carrier: carrierLabelMap[carrier] || carrier,
      restricted,
      used: Number(matched?.used || 0),
      limit: restricted ? Number(matched?.limit ?? limit) : null,
      remaining: restricted ? Math.max(Number(matched?.remaining ?? limit), 0) : null,
    };
  });
};

const renderUsageEmptyMessage = () => "No usage yet.";

const PORT_DISPLAY_RANGE = Array.from({ length: 64 }, (_, index) =>
  String(index + 1)
);

const getApiErrorMessage = (error, fallback) =>
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  error?.message ||
  fallback;

const formatImportErrors = (errors = []) =>
  (Array.isArray(errors) ? errors : [])
    .slice(0, 5)
    .map((item) => `Row ${Number(item.index) + 1}: ${item.error}`)
    .join(" | ");

const downloadCsvFile = (filename, headers, rows) => {
  const escapeCell = (value) => {
    const text = String(value ?? "");
    if (/[",\n]/.test(text)) {
      return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
  };
  const lines = [
    headers.map(escapeCell).join(","),
    ...rows.map((row) => row.map(escapeCell).join(",")),
  ];
  const blob = new Blob([`${lines.join("\n")}\n`], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const DclManager = () => {
  document.body.style.backgroundColor = grey[300];
  const history = useHistory();
  const classes = useStyles();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [status, setStatus] = useState("");
  const [statusSeverity, setStatusSeverity] = useState("info");
  const [simBoxesUnifiedSet, setSimBoxesUnifiedSet] = useState([]);

  const [globalConfig, setGlobalConfig] = useState(defaultGlobalConfig);
  const [savedGlobalConfig, setSavedGlobalConfig] = useState(defaultGlobalConfig);
  const [scopedForm, setScopedForm] = useState(defaultScopedForm);
  const [editingConfig, setEditingConfig] = useState(null);

  const [configs, setConfigs] = useState([]);
  const [configFilters, setConfigFilters] = useState(defaultFilters);
  const [selectedConfigKeys, setSelectedConfigKeys] = useState([]);
  const [configPage, setConfigPage] = useState(1);
  const [configLimit, setConfigLimit] = useState(24);
  const [configTotal, setConfigTotal] = useState(0);

  const [events, setEvents] = useState([]);
  const [eventFilters, setEventFilters] = useState(defaultEventFilters);
  const [eventPage, setEventPage] = useState(1);
  const [eventLimit, setEventLimit] = useState(24);
  const [eventTotal, setEventTotal] = useState(0);

  const [summary, setSummary] = useState([]);
  const [summaryFilters, setSummaryFilters] = useState(defaultSummaryFilters);
  const [summaryPage, setSummaryPage] = useState(1);
  const [summaryLimit, setSummaryLimit] = useState(24);
  const [summaryTotal, setSummaryTotal] = useState(0);

  const [csvText, setCsvText] = useState("");
  const [csvFileName, setCsvFileName] = useState("");
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvRows, setCsvRows] = useState([]);
  const [csvMapping, setCsvMapping] = useState(
    csvMappingFields.reduce((acc, field) => {
      acc[field] = field;
      return acc;
    }, {})
  );
  const [csvMode, setCsvMode] = useState("skip");

  const [confirmState, setConfirmState] = useState({
    open: false,
    title: "",
    content: "",
    onConfirm: null,
  });
  const [usageDialogConfig, setUsageDialogConfig] = useState(null);
  const [usageDialogSubPortTab, setUsageDialogSubPortTab] = useState("");

  const showStatus = useCallback((message, severity = "info") => {
    setStatus(message);
    setStatusSeverity(severity);
  }, []);

  const withLoading = useCallback(async (callback) => {
    setLoading(true);
    try {
      await callback();
    } finally {
      setLoading(false);
    }
  }, []);

  const requestConfigQuery = useMemo(
    () => ({
      ...configFilters,
      limit: configLimit,
      skip: (configPage - 1) * configLimit,
    }),
    [configFilters, configLimit, configPage]
  );

  const requestEventQuery = useMemo(
    () => ({
      ...eventFilters,
      limit: eventLimit,
      skip: (eventPage - 1) * eventLimit,
    }),
    [eventFilters, eventLimit, eventPage]
  );

  const requestSummaryQuery = useMemo(
    () => ({
      ...summaryFilters,
      limit: summaryLimit,
      skip: (summaryPage - 1) * summaryLimit,
    }),
    [summaryFilters, summaryLimit, summaryPage]
  );

  const configPages = Math.max(1, Math.ceil(configTotal / configLimit) || 1);
  const eventPages = Math.max(1, Math.ceil(eventTotal / eventLimit) || 1);
  const summaryPages = Math.max(1, Math.ceil(summaryTotal / summaryLimit) || 1);
  const scopeAllowedSubPorts = getScopedAllowedSubPorts(scopedForm.boxes);
  const configFilterAllowedSubPorts = getAllowedSubPorts(configFilters.box);
  const eventFilterAllowedSubPorts = getAllowedSubPorts(eventFilters.box);
  const summaryFilterAllowedSubPorts = getAllowedSubPorts(summaryFilters.box);

  const loadGlobalConfig = useCallback(async () => {
    const response = await axios.get(`${API}/dcl/config/global`, {
      withCredentials: true,
    });
    const normalized = response.data
      ? normalizeConfigFromApi(response.data)
      : defaultGlobalConfig;
    setGlobalConfig(normalized);
    setSavedGlobalConfig(normalized);
  }, []);

  const loadConfigs = useCallback(async () => {
    const response = await axios.get(`${API}/dcl/configs`, {
      params: requestConfigQuery,
      withCredentials: true,
    });
    setConfigs(Array.isArray(response.data?.configs) ? response.data.configs : []);
    setConfigTotal(Number(response.data?.total || 0));
  }, [requestConfigQuery]);

  const loadEvents = useCallback(async () => {
    const response = await axios.get(`${API}/dcl/events`, {
      params: requestEventQuery,
      withCredentials: true,
    });
    setEvents(Array.isArray(response.data?.events) ? response.data.events : []);
    setEventTotal(Number(response.data?.total || 0));
  }, [requestEventQuery]);

  const loadSummary = useCallback(async () => {
    const response = await axios.get(`${API}/dcl/events/summary`, {
      params: requestSummaryQuery,
      withCredentials: true,
    });
    setSummary(Array.isArray(response.data?.summary) ? response.data.summary : []);
    setSummaryTotal(Number(response.data?.total || 0));
  }, [requestSummaryQuery]);

  const loadAll = useCallback(async () => {
    await withLoading(async () => {
      await Promise.all([loadGlobalConfig(), loadConfigs(), loadEvents(), loadSummary()]);
    });
  }, [loadConfigs, loadEvents, loadGlobalConfig, loadSummary, withLoading]);

  const fetchSimBoxesUnified = useCallback(async () => {
    const res = await axios.get(`${API}/gateway/simboxes-unified`, {
      withCredentials: true,
    });
    setSimBoxesUnifiedSet(res.data.SimBoxesUnifiedSet || []);
  }, []);

  useEffect(() => {
    const getData = async () => {
      try {
        const user = localStorage.getItem("user");
        if (!user) {
          history.push("/");
          return;
        }
        await axios.get(process.env.REACT_APP_API_URL + `/users/${user}`, {
          withCredentials: true,
        });
        await Promise.all([loadAll(), fetchSimBoxesUnified()]);
        setLoaded(true);
      } catch (error) {
        history.push("/");
      }
    };
    getData();
  }, [history, loadAll, fetchSimBoxesUnified]);

  useEffect(() => {
    if (!loaded) return;
    loadConfigs();
  }, [loaded, loadConfigs]);

  useEffect(() => {
    if (!loaded) return;
    loadEvents();
  }, [loaded, loadEvents]);

  useEffect(() => {
    if (!loaded) return;
    loadSummary();
  }, [loaded, loadSummary]);

  useEffect(() => {
    if (!status) return undefined;
    const timer = setTimeout(() => {
      setStatus("");
    }, 4000);
    return () => clearTimeout(timer);
  }, [status]);

  useEffect(() => {
    setScopedForm((prev) => {
      const nextSubPorts = (Array.isArray(prev.subPorts) ? prev.subPorts : []).filter((subPort) =>
        scopeAllowedSubPorts.includes(String(subPort || ""))
      );
      if (nextSubPorts.length === (prev.subPorts || []).length) {
        return prev;
      }
      return {
        ...prev,
        subPorts: nextSubPorts,
      };
    });
  }, [scopeAllowedSubPorts]);

  if (!loaded) {
    return (
      <React.Fragment>
        <ProgrssBar />
        <h1>Please Wait....</h1>
      </React.Fragment>
    );
  }

  const openConfirm = (title, content, onConfirm) => {
    setConfirmState({
      open: true,
      title,
      content,
      onConfirm,
    });
  };

  const closeConfirm = () => {
    setConfirmState({
      open: false,
      title: "",
      content: "",
      onConfirm: null,
    });
  };

  const openUsageDialog = (config) => {
    setUsageDialogConfig(config);
    setUsageDialogSubPortTab("");
  };

  const closeUsageDialog = () => {
    setUsageDialogConfig(null);
    setUsageDialogSubPortTab("");
  };

  const buildScopedPayloads = () => {
    const carriers = normalizeUniqueUpperList(scopedForm.carriers);
    if (scopedForm.enabled !== false && !carriers.length) {
      throw new Error("At least one carrier is required.");
    }

    const boxes = [...new Set((Array.isArray(scopedForm.boxes) ? scopedForm.boxes : []).map((box) => String(box).trim()).filter(Boolean))];
    const subPorts = [...new Set((Array.isArray(scopedForm.subPorts) ? scopedForm.subPorts : []).map((subPort) => String(subPort).trim()).filter(Boolean))];
    const basePayload = {
      scope: scopedForm.scope,
      enabled: scopedForm.enabled,
      mode: scopedForm.mode,
      limit: Number(scopedForm.limit),
      carriers,
      fallback: scopedForm.fallback,
      countMode:
        scopedForm.scope === "box" || scopedForm.scope === "subport"
          ? scopedForm.countMode || "per_port"
          : undefined,
    };
    const payloads = [];
    let portData = null;

    if (scopedForm.scope === "port") {
      const ports = expandPortsInput(scopedForm.portsText);
      if (!ports.length) {
        throw new Error("Port scope requires at least one port.");
      }
      const safeNumberMode =
        scopedForm.mode === "reroute" ? scopedForm.safeNumberMode : "pool";
      if (scopedForm.mode === "reroute" && safeNumberMode === "mapping") {
        const parsed = parseSequentialMappings(ports, scopedForm.safeNumberMapText);
        if (parsed.numbers.length !== ports.length) {
          throw new Error(
            `Port mapping requires exactly ${ports.length} safe number line(s).`
          );
        }
        const invalid = parsed.numbers.filter((num) => !isValidSafeNumber(num));
        if (invalid.length) {
          throw new Error("All mapped safe numbers must be 11 digits starting with 1.");
        }
        portData = {
          ports,
          safeNumberMode,
          safeNumberMappings: parsed.mappings,
          safeNumbers: [],
        };
      } else {
        const safeNumbers = normalizeUniquePhones(
          parseDelimitedList(scopedForm.safeNumbersText)
        );
        const invalid = safeNumbers.filter((num) => !isValidSafeNumber(num));
        if (invalid.length) {
          throw new Error("Invalid safe numbers detected.");
        }
        portData = {
          ports,
          safeNumberMode,
          safeNumberMappings: [],
          safeNumbers,
        };
      }
    } else {
      const safeNumbers = normalizeUniquePhones(
        parseDelimitedList(scopedForm.safeNumbersText)
      );
      basePayload.safeNumberMode = "pool";
      basePayload.safeNumberMappings = [];
      basePayload.safeNumbers = safeNumbers;
    }

    if (basePayload.mode === "reroute") {
      if (portData?.safeNumberMode === "mapping") {
        if (!portData.safeNumberMappings?.length) {
          throw new Error("Reroute mapping mode requires safe number mappings.");
        }
      } else if (!(portData?.safeNumbers?.length || basePayload.safeNumbers?.length)) {
        throw new Error("Reroute mode requires at least one safe number.");
      }
    }

    if (!["global", "box", "subport", "port"].includes(basePayload.scope)) {
      throw new Error("Invalid scope selected.");
    }
    if ((basePayload.scope === "box" || basePayload.scope === "subport" || basePayload.scope === "port") && !boxes.length) {
      throw new Error("Select at least one box for scoped DCL.");
    }
    if ((basePayload.scope === "subport" || basePayload.scope === "port") && !subPorts.length) {
      throw new Error("Select at least one SubPort for this scope.");
    }

    if (basePayload.scope === "box") {
      boxes.forEach((box) => {
        payloads.push({
          ...basePayload,
          box,
        });
      });
    } else if (basePayload.scope === "subport") {
      boxes.forEach((box) => {
        const validSubPorts = subPorts.filter((subPort) =>
          getAllowedSubPorts(box).includes(String(subPort))
        );
        validSubPorts.forEach((subPort) => {
          payloads.push({
            ...basePayload,
            box,
            subPort,
          });
        });
      });
    } else if (basePayload.scope === "port") {
      boxes.forEach((box) => {
        const validSubPorts = subPorts.filter((subPort) =>
          getAllowedSubPorts(box).includes(String(subPort))
        );
        validSubPorts.forEach((subPort) => {
          payloads.push({
            ...basePayload,
            box,
            subPort,
            ports: portData.ports,
            safeNumberMode: portData.safeNumberMode,
            safeNumberMappings: portData.safeNumberMappings,
            safeNumbers: portData.safeNumbers,
          });
        });
      });
    }

    if (!payloads.length) {
      throw new Error("No valid scoped DCL targets could be generated from the selected boxes and subports.");
    }
    if (editingConfig && payloads.length !== 1) {
      throw new Error("Editing supports one exact scoped DCL target at a time. Reset the form to create multiple targets.");
    }

    if (editingConfig && payloads.length === 1) {
      payloads[0].existingTargetKey = editingConfig.targetKey;
    }

    return payloads;
  };

  const resetScopedForm = () => {
    setScopedForm(defaultScopedForm);
    setEditingConfig(null);
  };

  const handleSaveGlobal = async () => {
    const payload = {
      ...globalConfig,
      carriers: normalizeUniqueUpperList(globalConfig.carriers),
      safeNumbers: normalizeUniquePhones(parseDelimitedList(globalConfig.safeNumbersText)),
      safeNumberMode: "pool",
      safeNumberMappings: [],
      limit: Number(globalConfig.limit),
      cacheEnabled: globalConfig.cacheEnabled,
      cacheTtlMs: Number(globalConfig.cacheTtlMs),
    };
    if (payload.enabled !== false && payload.mode === "reroute" && !payload.safeNumbers.length) {
      showStatus("Global reroute mode requires safe numbers.", "warning");
      return;
    }
    try {
      await withLoading(async () => {
        await axios.put(`${API}/dcl/config/global`, payload, {
          withCredentials: true,
        });
        await loadGlobalConfig();
        showStatus("Global DCL config saved.", "success");
      });
    } catch (error) {
      showStatus(
        getApiErrorMessage(error, "Failed to save global DCL config."),
        "error"
      );
    }
  };

  const handleGlobalCarriersChange = (event) => {
    const value = normalizeCarrierSelection(event.target.value);
    setGlobalConfig((prev) => ({ ...prev, carriers: value }));
  };

  const handleScopedCarriersChange = (event) => {
    const value = normalizeCarrierSelection(event.target.value);
    setScopedForm((prev) => ({ ...prev, carriers: value }));
  };

  const handleSaveScoped = async () => {
    let payloads;
    try {
      payloads = buildScopedPayloads();
    } catch (error) {
      showStatus(error.message, "warning");
      return;
    }
    try {
      await withLoading(async () => {
        if (payloads.length === 1) {
          await axios.post(`${API}/dcl/config`, payloads[0], { withCredentials: true });
        } else {
          await axios.post(
            `${API}/dcl/config/bulk`,
            { mode: "strict", configs: payloads },
            { withCredentials: true }
          );
        }
        resetScopedForm();
        setConfigPage(1);
        await loadConfigs();
        showStatus(
          editingConfig
            ? "Scoped DCL config updated."
            : payloads.length > 1
            ? `Saved ${payloads.length} scoped DCL configs.`
            : "Scoped DCL config saved.",
          "success"
        );
      });
    } catch (error) {
      showStatus(
        getApiErrorMessage(error, "Failed to save scoped DCL config."),
        "error"
      );
    }
  };

  const handleEditConfig = (config) => {
    setEditingConfig(config);
    setScopedForm(buildScopedFormFromConfig(config));
    setTab(1);
  };

  const handleDeleteConfig = (config) => {
    openConfirm(
      "Delete DCL Config",
      "Are you sure you want to delete this DCL config?",
      async () => {
        closeConfirm();
        try {
          await withLoading(async () => {
            await axios.delete(`${API}/dcl/config`, {
              data: {
                targetKey: config.targetKey,
              },
              withCredentials: true,
            });
            setSelectedConfigKeys((prev) => prev.filter((item) => item !== config.targetKey));
            await loadConfigs();
            showStatus("DCL config deleted.", "success");
          });
        } catch (error) {
          showStatus(
            getApiErrorMessage(error, "Failed to delete DCL config."),
            "error"
          );
        }
      }
    );
  };

  const handleDeleteSelected = () => {
    if (!selectedConfigKeys.length) {
      showStatus("Select at least one config first.", "warning");
      return;
    }
    const selectedConfigs = configs.filter((item) =>
      selectedConfigKeys.includes(item.targetKey)
    );
    openConfirm(
      "Delete Selected DCL Configs",
      `Are you sure you want to delete ${selectedConfigs.length} selected DCL config(s)?`,
      async () => {
        closeConfirm();
        try {
          await withLoading(async () => {
            await axios.post(
              `${API}/dcl/config/bulk-delete`,
              {
                configs: selectedConfigs.map((item) => ({
                  targetKey: item.targetKey,
                })),
              },
              { withCredentials: true }
            );
            setSelectedConfigKeys([]);
            await loadConfigs();
            showStatus("Selected DCL configs deleted.", "success");
          });
        } catch (error) {
          showStatus(
            getApiErrorMessage(error, "Failed to delete selected DCL configs."),
            "error"
          );
        }
      }
    );
  };

  const handleDeleteAllFiltered = () => {
    openConfirm(
      "Delete Filtered DCL Configs",
      "Are you sure you want to delete all currently filtered scoped DCL configs?",
      async () => {
        closeConfirm();
        try {
          await withLoading(async () => {
            await axios.post(
              `${API}/dcl/config/delete-all`,
              {
                scope: configFilters.scope || undefined,
                box: configFilters.box || undefined,
                subPort: configFilters.subPort || undefined,
                port: configFilters.port || undefined,
              },
              { withCredentials: true }
            );
            setSelectedConfigKeys([]);
            await loadConfigs();
            showStatus("Filtered DCL configs deleted.", "success");
          });
        } catch (error) {
          showStatus(
            getApiErrorMessage(error, "Failed to delete filtered DCL configs."),
            "error"
          );
        }
      }
    );
  };

  const handleImportCsv = async () => {
    if (!csvRows.length) {
      showStatus("Choose a CSV file before import.", "warning");
      return;
    }

    const readMapped = (row, field) => {
      const header = csvMapping[field];
      if (!header) return "";
      return row?.[header] ?? "";
    };

    const configs = csvRows.map((row) => {
      const scope = String(readMapped(row, "scope") || "").trim().toLowerCase();
      const ports = scope === "port"
        ? expandPortsInput(readMapped(row, "ports") || readMapped(row, "port") || "")
        : [];
      const safeNumbers = normalizeUniquePhones(
        parseDelimitedList(readMapped(row, "safeNumbers") || readMapped(row, "safeNumber") || "")
      );
      const declaredSafeMode = String(readMapped(row, "safeNumberMode") || "pool")
        .trim()
        .toLowerCase();
      const hasExplicitMappings = Boolean(String(readMapped(row, "safeNumberMappings") || "").trim());
      const safeNumberMode =
        declaredSafeMode === "mapping" || hasExplicitMappings ? "mapping" : "pool";
      const safeNumberMappings =
        safeNumberMode === "mapping"
          ? String(readMapped(row, "safeNumberMappings") || "")
              .split("|")
              .map((item) => item.trim())
              .filter(Boolean)
              .map((item) => {
                const [port, phone] = item.split(":");
                return {
                  port: String(port || "").trim(),
                  phone: normalizePhone(phone),
                };
              })
          : [];

      return {
        scope,
        box: readMapped(row, "box") || undefined,
        subPort: readMapped(row, "subPort") || undefined,
        ports,
        enabled: String(readMapped(row, "enabled") || "true").toLowerCase() !== "false",
        mode: String(readMapped(row, "mode") || "skip").toLowerCase(),
        limit: Number(readMapped(row, "limit") || 0),
        carriers: normalizeUniqueUpperList(parseDelimitedList(readMapped(row, "carriers") || "")),
        countMode:
          scope === "box" || scope === "subport"
            ? String(readMapped(row, "countMode") || "per_port").trim().toLowerCase()
            : undefined,
        safeNumberMode,
        safeNumbers,
        safeNumberMappings,
        fallback: String(readMapped(row, "fallback") || "backwash").trim(),
      };
    });

    try {
      await withLoading(async () => {
        const response = await axios.post(
          `${API}/dcl/config/bulk`,
          { mode: csvMode, configs },
          { withCredentials: true }
        );
        const processed = Number(response.data?.processed || 0);
        const errors = Array.isArray(response.data?.errors) ? response.data.errors : [];
        const skipped = errors.length;
        setCsvText("");
        setCsvFileName("");
        setCsvHeaders([]);
        setCsvRows([]);
        setConfigPage(1);
        await loadConfigs();
        if (skipped) {
          showStatus(
            `Imported ${processed} row(s), skipped ${skipped}. ${formatImportErrors(errors)}`,
            "warning"
          );
        } else {
          showStatus(`Imported ${processed} DCL row(s).`, "success");
        }
      });
    } catch (error) {
      const importErrors = error?.response?.data?.errors;
      showStatus(
        importErrors?.length
          ? `${getApiErrorMessage(error, "Failed to import DCL CSV.")}. ${formatImportErrors(
              importErrors
            )}`
          : getApiErrorMessage(error, "Failed to import DCL CSV."),
        "error"
      );
    }
  };

  const handleCsvFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = parseCsv(text);
      const nextMapping = csvMappingFields.reduce((acc, field) => {
        const found = parsed.headers.find(
          (header) => String(header).trim().toLowerCase() === field.toLowerCase()
        );
        acc[field] = found || "";
        return acc;
      }, {});
      setCsvText(text);
      setCsvFileName(file.name || "selected.csv");
      setCsvHeaders(parsed.headers);
      setCsvRows(parsed.rows);
      setCsvMapping(nextMapping);
      showStatus(`Loaded CSV file: ${file.name}`, "info");
    } catch (error) {
      setCsvText("");
      setCsvFileName("");
      setCsvHeaders([]);
      setCsvRows([]);
      showStatus("Failed to read selected CSV file.", "error");
    } finally {
      event.target.value = "";
    }
  };

  const handleCsvMappingChange = (field) => (event) => {
    const value = event.target.value;
    setCsvMapping((prev) => ({ ...prev, [field]: value }));
  };

  const exportEventsCsv = async () => {
    try {
      await withLoading(async () => {
        const headers = [
          "When",
          "Scope",
          "Box",
          "SubPort",
          "Port",
          "Carrier",
          "Action",
          "Mode",
          "Limit",
          "Reason",
          "Phone",
          "Reroute",
        ];
        const limit = 500;
        let skip = 0;
        let total = 0;
        let rows = [];
        do {
          const params = { limit, skip };
          if (eventFilters.box) params.box = eventFilters.box;
          if (eventFilters.subPort) params.subPort = eventFilters.subPort;
          if (eventFilters.port) params.port = eventFilters.port;
          if (eventFilters.carrier) params.carrier = eventFilters.carrier;
          if (eventFilters.action) params.action = eventFilters.action;
          const response = await axios.get(`${API}/dcl/events`, {
            params,
            withCredentials: true,
          });
          const batch = Array.isArray(response.data?.events) ? response.data.events : [];
          total = Number(response.data?.total || 0);
          rows = rows.concat(batch);
          skip += batch.length;
          if (!batch.length) break;
        } while (skip < total);

        const csvRows = rows.map((event) => [
          event.createdAt ? new Date(event.createdAt).toLocaleString() : "-",
          humanizeToken(event.scope),
          event.box || "-",
          event.subPort || "-",
          event.port || "-",
          humanizeToken(event.carrier),
          humanizeToken(event.action),
          humanizeToken(event.mode),
          event.limit ?? "-",
          event.reason || "-",
          event.phone || "-",
          event.reroutePhone || "-",
        ]);
        downloadCsvFile("dcl-events.csv", headers, csvRows);
      });
    } catch (error) {
      showStatus("Failed to download DCL events.", "error");
    }
  };

  const exportSummaryCsv = async () => {
    try {
      await withLoading(async () => {
        const headers = ["Box", "SubPort", "Port", "Carrier", "Action", "Count"];
        const limit = 500;
        let skip = 0;
        let total = 0;
        let rows = [];
        do {
          const params = { limit, skip };
          if (summaryFilters.box) params.box = summaryFilters.box;
          if (summaryFilters.subPort) params.subPort = summaryFilters.subPort;
          if (summaryFilters.port) params.port = summaryFilters.port;
          if (summaryFilters.carrier) params.carrier = summaryFilters.carrier;
          if (summaryFilters.action) params.action = summaryFilters.action;
          const response = await axios.get(`${API}/dcl/events/summary`, {
            params,
            withCredentials: true,
          });
          const batch = Array.isArray(response.data?.summary) ? response.data.summary : [];
          total = Number(response.data?.total || 0);
          rows = rows.concat(batch);
          skip += batch.length;
          if (!batch.length) break;
        } while (skip < total);

        const csvRows = rows.map((item) => [
          item?._id?.box || "-",
          item?._id?.subPort || "-",
          item?._id?.port || "-",
          humanizeToken(item?._id?.carrier),
          humanizeToken(item?._id?.action),
          item?.count ?? 0,
        ]);
        downloadCsvFile("dcl-summary.csv", headers, csvRows);
      });
    } catch (error) {
      showStatus("Failed to download DCL summary.", "error");
    }
  };

  const exportConfigsCsv = async () => {
    try {
      await withLoading(async () => {
        const headers = [
          "scope",
          "box",
          "subPort",
          "port",
          "enabled",
          "mode",
          "limit",
          "carriers",
          "countMode",
          "safeNumberMode",
          "safeNumbers",
          "safeNumberMappings",
          "fallback",
          "usage",
        ];
        const limit = 500;
        let skip = 0;
        let total = 0;
        let rows = [];
        do {
          const params = { limit, skip };
          if (configFilters.scope) params.scope = configFilters.scope;
          if (configFilters.box) params.box = configFilters.box;
          if (configFilters.subPort) params.subPort = configFilters.subPort;
          if (configFilters.port) params.port = configFilters.port;
          const response = await axios.get(`${API}/dcl/configs`, {
            params,
            withCredentials: true,
          });
          const batch = Array.isArray(response.data?.configs) ? response.data.configs : [];
          total = Number(response.data?.total || 0);
          rows = rows.concat(batch);
          skip += batch.length;
          if (!batch.length) break;
        } while (skip < total);

        const csvRows = rows.map((config) => {
          const safeNumberMode =
            config?.mode === "reroute" ? config?.safeNumberMode || "pool" : "";
          const safeNumbers = Array.isArray(config?.safeNumbers)
            ? config.safeNumbers.join("|")
            : "";
          const safeNumberMappings =
            config?.mode === "reroute" &&
            safeNumberMode === "mapping" &&
            Array.isArray(config?.safeNumberMappings) &&
            config.safeNumberMappings.length
              ? config.safeNumberMappings
                  .map((item) => `${item.port}:${item.phone}`)
                  .join("|")
              : "";
          const usageText = renderUsageSummary(config).length
            ? renderUsageSummary(config)
                .map((item) =>
                  item.countMode === "per_port"
                    ? `${item.carrier}:${(item.portUsages || [])
                        .map((portUsage) => `${portUsage.label}:${portUsage.used}/${portUsage.limit}(${portUsage.remaining} left)`)
                        .join(";")}`
                    : `${item.carrier}:${item.used}/${item.limit}(${item.remaining} left)`
                )
                .join("|")
            : "";

          return [
            config?.scope || "",
            config?.box || "",
            config?.subPort || "",
            config?.port || "",
            config?.enabled === false ? "false" : "true",
            config?.mode || "",
            config?.limit ?? "",
            Array.isArray(config?.carriers) ? config.carriers.join("|") : "",
            config?.scope === "box" || config?.scope === "subport"
              ? config?.countMode || "shared"
              : "",
            safeNumberMode,
            safeNumbers,
            safeNumberMappings,
            config?.fallback || "",
            usageText,
          ];
        });
        downloadCsvFile("dcl-configs.csv", headers, csvRows);
      });
    } catch (error) {
      showStatus("Failed to download DCL configs.", "error");
    }
  };

  const renderSafeNumberHelper = () => {
    if (scopedForm.scope !== "port" || scopedForm.safeNumberMode !== "mapping") {
      return (
        <Typography variant="body2" className={classes.subtleText}>
          Shared pool mode applies the same safe-number list to the whole rule.
        </Typography>
      );
    }

    const ports = expandPortsInput(scopedForm.portsText);
    return (
      <Box className={classes.helperBox}>
        <Typography variant="body2" style={{ fontWeight: 600 }}>
          Port mapping mode
        </Typography>
        <Typography variant="body2" className={classes.subtleText}>
          Selected ports: {ports.length ? ports.join(", ") : "none"}
        </Typography>
        <Typography variant="body2" className={classes.subtleText}>
          Paste one safe number per line. The first line maps to the first selected
          port, the second line to the second port, and so on.
        </Typography>
      </Box>
    );
  };

  return (
    <Container maxWidth="xl" style={{ marginTop: 24 }}>
      {loading && <ProgrssBar />}
      {status ? (
        <Box mb={2}>
          <Alert severity={statusSeverity} onClose={() => setStatus("")}>
            {status}
          </Alert>
        </Box>
      ) : null}

      <Paper className={classes.paper}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="nowrap"
        >
          <Tabs
            value={tab}
            onChange={(_, value) => setTab(value)}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Global DCL" />
            <Tab label="Scoped DCL" />
            <Tab label="Configs" />
            <Tab label="CSV Import" />
            <Tab label="Events" />
            <Tab label="Summary" />
          </Tabs>
          <Typography
            variant="subtitle1"
            style={{ fontWeight: 600, color: "#475569", whiteSpace: "nowrap", marginLeft: 16 }}
          >
            Daily Carrier Limit
          </Typography>
        </Box>
      </Paper>

      {tab === 0 && (
        <Box mt={3}>
          <Paper className={classes.paper}>
            <Box className={classes.sectionHeader}>
              <Typography className={classes.sectionTitle} style={{ marginBottom: 0 }}>
                Global DCL Settings
              </Typography>
              <FormControlLabel
                className={classes.compactSwitch}
                control={
                  <Switch
                    color="primary"
                    checked={globalConfig.enabled}
                    onChange={(e) =>
                      setGlobalConfig((prev) => ({
                        ...prev,
                        enabled: e.target.checked,
                      }))
                    }
                  />
                }
                label="Enabled"
              />
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Mode</InputLabel>
                  <Select
                    value={globalConfig.mode}
                    onChange={(e) =>
                      setGlobalConfig((prev) => ({ ...prev, mode: e.target.value }))
                    }
                    label="Mode"
                  >
                    <MenuItem value="skip">Skip</MenuItem>
                    <MenuItem value="reroute">Reroute</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  label="Daily Limit"
                  type="number"
                  value={globalConfig.limit}
                  onChange={(e) =>
                    setGlobalConfig((prev) => ({ ...prev, limit: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Fallback</InputLabel>
                  <Select
                    value={globalConfig.fallback}
                    onChange={(e) =>
                      setGlobalConfig((prev) => ({
                        ...prev,
                        fallback: e.target.value,
                      }))
                    }
                    label="Fallback"
                  >
                    <MenuItem value="backwash">Backwash</MenuItem>
                    <MenuItem value="dontSend">DontSend</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Carriers</InputLabel>
                  <Select
                    multiple
                    value={globalConfig.carriers}
                    onChange={handleGlobalCarriersChange}
                    renderValue={(selected) =>
                      selected.map((value) => carrierLabelMap[value] || value).join(", ")
                    }
                    label="Carriers"
                    MenuProps={{
                      getContentAnchorEl: null,
                      anchorOrigin: { vertical: "bottom", horizontal: "left" },
                      transformOrigin: { vertical: "top", horizontal: "left" },
                    }}
                  >
                    {carrierOptions.map((carrier) => (
                      <MenuItem
                        key={carrier.value}
                        value={carrier.value}
                        disabled={
                          carrier.value !== "ALL" &&
                          globalConfig.carriers.includes("ALL")
                        }
                      >
                        <Checkbox checked={globalConfig.carriers.indexOf(carrier.value) > -1} />
                        <ListItemText primary={carrier.label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {globalConfig.mode === "reroute" && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Safe Numbers (shared pool)"
                    helperText="Paste one per line, or separate with commas or pipes."
                    value={globalConfig.safeNumbersText}
                    onChange={(e) =>
                      setGlobalConfig((prev) => ({
                        ...prev,
                        safeNumbersText: e.target.value,
                      }))
                    }
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" flexWrap="wrap" gridGap={24}>
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        checked={globalConfig.cacheEnabled}
                        onChange={(e) =>
                          setGlobalConfig((prev) => ({
                            ...prev,
                            cacheEnabled: e.target.checked,
                          }))
                        }
                      />
                    }
                    label="Cache Enabled"
                  />
                  <TextField
                    label="Cache TTL (ms)"
                    type="number"
                    value={globalConfig.cacheTtlMs}
                    onChange={(e) =>
                      setGlobalConfig((prev) => ({
                        ...prev,
                        cacheTtlMs:
                          e.target.value === "" ? "" : Number(e.target.value),
                      }))
                    }
                    style={{ width: 320, maxWidth: "100%" }}
                  />
                </Box>
              </Grid>
              {renderGlobalUsageSummary(savedGlobalConfig).length ? (
                <Grid item xs={12}>
                  <Box className={classes.helperBox}>
                    <Box className={classes.carrierStatusGrid}>
                      {renderGlobalUsageSummary(savedGlobalConfig).map((item) => (
                        <Box
                          key={item.carrier}
                          className={`${classes.carrierStatusCard} ${
                            item.restricted ? "" : classes.carrierStatusCardMuted
                          }`}
                        >
                          <Typography className={classes.carrierStatusName}>
                            {item.carrier}
                          </Typography>
                          <Typography className={classes.carrierStatusValue}>
                            {item.restricted
                              ? `${item.used}/${item.limit} used, ${item.remaining} left`
                              : "∞ No limit"}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Grid>
              ) : null}
            </Grid>
            <Box className={classes.actionsRow}>
              <Button color="primary" variant="contained" onClick={handleSaveGlobal}>
                Save Global DCL
              </Button>
            </Box>
          </Paper>
        </Box>
      )}

      {tab === 1 && (
        <Box mt={3}>
          <Paper className={classes.paper}>
            <Box className={classes.sectionHeader}>
              <Typography className={classes.sectionTitle} style={{ marginBottom: 0 }}>
                {editingConfig ? "Edit Scoped DCL Config" : "Add Scoped DCL Config"}
              </Typography>
              <FormControlLabel
                className={classes.compactSwitch}
                control={
                  <Switch
                    color="primary"
                    checked={scopedForm.enabled}
                    onChange={(e) =>
                      setScopedForm((prev) => ({
                        ...prev,
                        enabled: e.target.checked,
                      }))
                    }
                  />
                }
                label="Enabled"
              />
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={scopedForm.scope === "box" ? 3 : 3}>
                <FormControl fullWidth>
                  <InputLabel>Scope</InputLabel>
                  <Select
                    value={scopedForm.scope}
                    onChange={(e) =>
                      setScopedForm((prev) => ({
                        ...prev,
                        scope: e.target.value,
                        countMode:
                          e.target.value === "box" || e.target.value === "subport"
                            ? prev.countMode || "per_port"
                            : "per_port",
                        safeNumberMode:
                          e.target.value === "port" ? prev.safeNumberMode : "pool",
                      }))
                    }
                    label="Scope"
                  >
                    <MenuItem value="box">Box</MenuItem>
                    <MenuItem value="subport">SubPort</MenuItem>
                    <MenuItem value="port">Port</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={scopedForm.scope === "box" ? 9 : scopedForm.scope === "subport" ? 5 : 3}>
                <FormControl fullWidth>
                  <InputLabel>Box</InputLabel>
                  <Select
                    multiple
                    value={scopedForm.boxes}
                    onChange={(e) =>
                      setScopedForm((prev) => ({
                        ...prev,
                        boxes: Array.isArray(e.target.value) ? e.target.value : [e.target.value],
                      }))
                    }
                    renderValue={(selected) => (Array.isArray(selected) ? selected.join(", ") : "")}
                    label="Box"
                  >
                    {simBoxesUnifiedSet.map((box) => (
                      <MenuItem key={box} value={box}>
                        <Checkbox checked={scopedForm.boxes.indexOf(box) > -1} />
                        <ListItemText primary={box} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {scopedForm.scope !== "box" && (
                <Grid item xs={12} md={scopedForm.scope === "subport" ? 4 : 3}>
                  <FormControl fullWidth>
                    <InputLabel>SubPort</InputLabel>
                    <Select
                      multiple
                      value={scopedForm.subPorts}
                      onChange={(e) =>
                        setScopedForm((prev) => ({
                          ...prev,
                          subPorts: Array.isArray(e.target.value)
                            ? e.target.value
                            : [e.target.value],
                        }))
                      }
                      renderValue={(selected) =>
                        Array.isArray(selected) ? selected.join(", ") : ""
                      }
                      label="SubPort"
                    >
                      {scopeAllowedSubPorts.map((sp) => (
                        <MenuItem key={sp} value={String(sp)}>
                          <Checkbox checked={scopedForm.subPorts.indexOf(String(sp)) > -1} />
                          <ListItemText primary={sp} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              {scopedForm.scope === "port" && (
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Ports"
                    fullWidth
                    helperText="Use single values, commas, or ranges like 1-64."
                    value={scopedForm.portsText}
                    onChange={(e) =>
                      setScopedForm((prev) => ({ ...prev, portsText: e.target.value }))
                    }
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Carriers</InputLabel>
                  <Select
                    multiple
                    value={scopedForm.carriers}
                    onChange={handleScopedCarriersChange}
                    renderValue={(selected) =>
                      selected.map((value) => carrierLabelMap[value] || value).join(", ")
                    }
                    label="Carriers"
                    MenuProps={{
                      getContentAnchorEl: null,
                      anchorOrigin: { vertical: "bottom", horizontal: "left" },
                      transformOrigin: { vertical: "top", horizontal: "left" },
                    }}
                  >
                    {carrierOptions.map((carrier) => (
                      <MenuItem
                        key={carrier.value}
                        value={carrier.value}
                        disabled={
                          carrier.value !== "ALL" &&
                          scopedForm.carriers.includes("ALL")
                        }
                      >
                        <Checkbox checked={scopedForm.carriers.indexOf(carrier.value) > -1} />
                        <ListItemText primary={carrier.label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {(scopedForm.scope === "box" || scopedForm.scope === "subport") && (
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Count Mode</InputLabel>
                    <Select
                      value={scopedForm.countMode}
                      onChange={(e) =>
                        setScopedForm((prev) => ({
                          ...prev,
                          countMode: e.target.value,
                        }))
                      }
                      label="Count Mode"
                    >
                      <MenuItem value="per_port">Per Port</MenuItem>
                      <MenuItem value="shared">Shared</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Mode</InputLabel>
                  <Select
                    value={scopedForm.mode}
                    onChange={(e) =>
                      setScopedForm((prev) => ({ ...prev, mode: e.target.value }))
                    }
                    label="Mode"
                  >
                    <MenuItem value="skip">Skip</MenuItem>
                    <MenuItem value="reroute">Reroute</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Daily Limit"
                  type="number"
                  value={scopedForm.limit}
                  onChange={(e) =>
                    setScopedForm((prev) => ({ ...prev, limit: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Fallback</InputLabel>
                  <Select
                    value={scopedForm.fallback}
                    onChange={(e) =>
                      setScopedForm((prev) => ({
                        ...prev,
                        fallback: e.target.value,
                      }))
                    }
                    label="Fallback"
                  >
                    <MenuItem value="backwash">Backwash</MenuItem>
                    <MenuItem value="dontSend">DontSend</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {scopedForm.mode === "reroute" && (
                <React.Fragment>
                  <Grid item xs={12} md={3}>
                    <FormControl
                      fullWidth
                      disabled={scopedForm.scope !== "port"}
                    >
                      <InputLabel>Safe Number Mode</InputLabel>
                      <Select
                        value={scopedForm.scope === "port" ? scopedForm.safeNumberMode : "pool"}
                        onChange={(e) =>
                          setScopedForm((prev) => ({
                            ...prev,
                            safeNumberMode: e.target.value,
                          }))
                        }
                        label="Safe Number Mode"
                      >
                        <MenuItem value="pool">Shared Pool</MenuItem>
                        <MenuItem value="mapping">Port Mapping</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={9}>
                    <Box pt={1}>{renderSafeNumberHelper()}</Box>
                  </Grid>
                  {scopedForm.scope === "port" && scopedForm.safeNumberMode === "mapping" ? (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={8}
                        label="Safe Number Mapping"
                        helperText="Paste one safe number per line in the same order as the selected ports."
                        value={scopedForm.safeNumberMapText}
                        onChange={(e) =>
                          setScopedForm((prev) => ({
                            ...prev,
                            safeNumberMapText: e.target.value,
                          }))
                        }
                      />
                    </Grid>
                  ) : (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={5}
                        label="Safe Numbers"
                        helperText="Paste one per line, or separate with commas or pipes."
                        value={scopedForm.safeNumbersText}
                        onChange={(e) =>
                          setScopedForm((prev) => ({
                            ...prev,
                            safeNumbersText: e.target.value,
                          }))
                        }
                      />
                    </Grid>
                  )}
                </React.Fragment>
              )}
            </Grid>

            <Box className={classes.actionsRow} display="flex" gridGap={12}>
              <Button color="primary" variant="contained" onClick={handleSaveScoped}>
                {editingConfig ? "Update Scoped DCL" : "Save Scoped DCL"}
              </Button>
              <Button
                color="default"
                variant="outlined"
                onClick={resetScopedForm}
                style={{ backgroundColor: grey[100] }}
              >
                Reset
              </Button>
            </Box>
          </Paper>
        </Box>
      )}

      {tab === 2 && (
        <Box mt={3}>
          <Paper className={classes.paper}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Scoped DCL Configs
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Scope</InputLabel>
                  <Select
                    value={configFilters.scope}
                    onChange={(e) => {
                      setConfigPage(1);
                      setConfigFilters((prev) => ({ ...prev, scope: e.target.value }));
                    }}
                    label="Scope"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="box">Box</MenuItem>
                    <MenuItem value="subport">SubPort</MenuItem>
                    <MenuItem value="port">Port</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Box</InputLabel>
                  <Select
                    value={configFilters.box}
                    onChange={(e) => {
                      setConfigPage(1);
                      setConfigFilters((prev) => ({ ...prev, box: e.target.value, subPort: "" }));
                    }}
                    label="Box"
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {simBoxesUnifiedSet.map((box) => (
                      <MenuItem key={box} value={box}>
                        {box}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>SubPort</InputLabel>
                  <Select
                    value={configFilters.subPort}
                    onChange={(e) => {
                      setConfigPage(1);
                      setConfigFilters((prev) => ({ ...prev, subPort: e.target.value }));
                    }}
                    label="SubPort"
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {configFilterAllowedSubPorts.map((sp) => (
                      <MenuItem key={sp} value={String(sp)}>
                        {sp}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Port"
                  value={configFilters.port}
                  onChange={(e) => {
                    setConfigPage(1);
                    setConfigFilters((prev) => ({ ...prev, port: e.target.value }));
                  }}
                />
              </Grid>
            </Grid>
            <Box mt={2} className={classes.configsToolbar}>
              <Box className={classes.configsToolbarActions}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={exportConfigsCsv}
                >
                  Download CSV
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    setSelectedConfigKeys([]);
                    loadConfigs();
                  }}
                >
                  Refresh
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleDeleteSelected}
                >
                  Delete Selected
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleDeleteAllFiltered}
                >
                  Delete Filtered
                </Button>
              </Box>
            </Box>
            {configTotal > 0 && (
              <Pagination
                pages={configPages}
                currentPage={configPage}
                handlePage={setConfigPage}
                currentLimit={configLimit}
                handleLimit={(value) => {
                  setConfigPage(1);
                  setConfigLimit(Number(value));
                }}
                totalCounts={configTotal}
                showHead="true"
              />
            )}
            <Box mt={2} className={classes.tableWrap}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" className={classes.tableHeadCell}>
                      <Checkbox
                        color="default"
                        indeterminate={
                          selectedConfigKeys.length > 0 &&
                          selectedConfigKeys.length < configs.length
                        }
                        checked={
                          configs.length > 0 &&
                          selectedConfigKeys.length === configs.length
                        }
                        onChange={(e) =>
                          setSelectedConfigKeys(
                            e.target.checked ? configs.map((item) => item.targetKey) : []
                          )
                        }
                      />
                    </TableCell>
                    {["Scope", "Box", "SubPort", "Port", "Mode", "Limit", "Carriers", "Count Mode", "Usage", "Safe Mode", "Safe Targets", "Enabled", "Actions"].map(
                      (label) => (
                        <TableCell key={label} className={classes.tableHeadCell}>
                          {label}
                        </TableCell>
                      )
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {configs.length ? (
                    configs.map((config) => (
                      <TableRow key={config.targetKey} className={classes.tableRow}>
                        <TableCell padding="checkbox" className={classes.tableCheckboxCell}>
                          <Checkbox
                            color="primary"
                            checked={selectedConfigKeys.includes(config.targetKey)}
                            onChange={(e) =>
                              setSelectedConfigKeys((prev) =>
                                e.target.checked
                                  ? [...prev, config.targetKey]
                                  : prev.filter((item) => item !== config.targetKey)
                              )
                            }
                          />
                        </TableCell>
                        <TableCell>{humanizeToken(config.scope)}</TableCell>
                        <TableCell>{config.box || "-"}</TableCell>
                        <TableCell>{config.subPort || "-"}</TableCell>
                        <TableCell>{config.port || "-"}</TableCell>
                        <TableCell>{humanizeToken(config.mode)}</TableCell>
                        <TableCell>{config.limit}</TableCell>
                        <TableCell>
                          {Array.isArray(config.carriers) && config.carriers.length ? (
                            <Box className={classes.configsTableCarrierBox}>
                              <Typography className={classes.configsTableCarrierPrimary}>
                                {expandCarriersForDisplay(config.carriers).length} carrier
                                {expandCarriersForDisplay(config.carriers).length === 1 ? "" : "s"}
                              </Typography>
                              <Typography className={classes.configsTableCarrierSecondary}>
                                {expandCarriersForDisplay(config.carriers)
                                  .map((carrier) => carrierLabelMap[carrier] || carrier)
                                  .join(", ")}
                              </Typography>
                            </Box>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell>
                          {config.scope === "box" || config.scope === "subport"
                            ? (
                              <Typography className={classes.configsTablePill}>
                                {humanizeToken(config.countMode || "shared")}
                              </Typography>
                            )
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {renderUsageSummary(config).length ? (
                            <Box style={{ maxHeight: 180, overflow: "auto" }}>
                              {(() => {
                                const perPortSummary = summarizePerPortUsage(config);
                                if (perPortSummary) {
                                  return (
                                    <Box className={classes.configsTableUsageBox}>
                                      <Box className={classes.usageCompactMetricLine}>
                                        <Typography
                                          component="span"
                                          className={`${classes.usageCompactMetric} ${classes.usageCompactMetricTracked}`}
                                        >
                                          <span className={classes.usageCompactMetricValue}>
                                            {perPortSummary.trackedPortCount}
                                          </span>
                                          <span className={classes.usageCompactMetricLabel}>
                                            tracked
                                          </span>
                                        </Typography>
                                        <Typography
                                          component="span"
                                          className={`${classes.usageCompactMetric} ${classes.usageCompactMetricActive}`}
                                        >
                                          <span className={classes.usageCompactMetricValue}>
                                            {perPortSummary.activeCount}
                                          </span>
                                          <span className={classes.usageCompactMetricLabel}>
                                            active
                                          </span>
                                        </Typography>
                                        <Typography
                                          component="span"
                                          className={`${classes.usageCompactMetric} ${classes.usageCompactMetricExhausted}`}
                                        >
                                          <span className={classes.usageCompactMetricValue}>
                                            {perPortSummary.exhaustedCount}
                                          </span>
                                          <span className={classes.usageCompactMetricLabel}>
                                            exhausted
                                          </span>
                                        </Typography>
                                      </Box>
                                      <Button
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        className={classes.usageCompactButton}
                                        onClick={() => openUsageDialog(config)}
                                      >
                                        View Ports
                                      </Button>
                                    </Box>
                                  );
                                }

                                return (
                                  <Box className={classes.configsTableUsageBox}>
                                    {renderUsageSummary(config).map((item) => (
                                      <Typography
                                        key={item.carrier}
                                        variant="body2"
                                        className={classes.configsTableUsageSharedLine}
                                      >
                                        {item.carrier}: {item.used}/{item.limit}{" "}
                                        <span
                                          className={`${classes.configsTableUsageSharedRemaining} ${getRemainingToneClass(
                                            item.remaining,
                                            item.limit,
                                            classes
                                          )}`}
                                        >
                                          ({item.remaining} left)
                                        </span>
                                      </Typography>
                                    ))}
                                  </Box>
                                );
                              })()}
                            </Box>
                          ) : Array.isArray(config.carriers) && config.carriers.length ? (
                            renderUsageEmptyMessage(config)
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell>
                          {config.mode === "reroute"
                            ? humanizeToken(config.safeNumberMode || "pool")
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {renderSafeTargetsSummary(config).length ? (
                            <Box style={{ maxHeight: 120, overflow: "auto" }}>
                              {renderSafeTargetsSummary(config).map((item) => (
                                <Typography
                                  key={item}
                                  variant="body2"
                                  style={{ whiteSpace: "nowrap" }}
                                >
                                  {item}
                                </Typography>
                              ))}
                            </Box>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography
                            component="span"
                            className={
                              config.enabled === false
                                ? classes.configsDisabledBadge
                                : classes.configsEnabledBadge
                            }
                          >
                            {config.enabled === false ? "Disabled" : "Enabled"}
                          </Typography>
                        </TableCell>
                        <TableCell className={classes.configsTableActionCell}>
                          <Box className={classes.configsTableActionLinks}>
                            <Button
                              size="small"
                              color="primary"
                              className={`${classes.configsActionButton} ${classes.configsEditButton}`}
                              onClick={() => handleEditConfig(config)}
                            >
                              Edit
                            </Button>
                            <Button
                              size="small"
                              color="secondary"
                              className={`${classes.configsActionButton} ${classes.configsDeleteButton}`}
                              onClick={() => handleDeleteConfig(config)}
                            >
                              Delete
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={12} align="center" className={classes.emptyCell}>
                        No DCL configs found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
            {configTotal > 0 && (
              <Pagination
                pages={configPages}
                currentPage={configPage}
                handlePage={setConfigPage}
                currentLimit={configLimit}
                handleLimit={(value) => {
                  setConfigPage(1);
                  setConfigLimit(Number(value));
                }}
                totalCounts={configTotal}
                showHead="false"
              />
            )}
          </Paper>
        </Box>
      )}

      {tab === 3 && (
        <Box mt={3}>
          <Paper className={classes.paper}>
            <Typography variant="h6" className={classes.sectionTitle}>
              CSV Import
            </Typography>
            <Typography variant="body2" className={classes.subtleText} gutterBottom>
              Import scoped DCL rules in bulk. For exact port rows, use the
              `port` column and put a single reroute number in `safeNumbers`
              (or `safeNumber`) when mode is `reroute`.
            </Typography>
            <Typography variant="body2" className={classes.subtleText} gutterBottom>
              Carrier overlap is not allowed on the same exact target. In `skip`
              mode, safe-number fields are ignored.
            </Typography>
            <Box mb={2}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  const blob = new Blob([buildSampleCsv()], { type: "text/csv;charset=utf-8" });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.href = url;
                  link.download = "dcl-sample.csv";
                  link.click();
                  URL.revokeObjectURL(url);
                }}
              >
                Download Sample CSV
              </Button>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Import Mode</InputLabel>
                  <Select
                    value={csvMode}
                    onChange={(e) => setCsvMode(e.target.value)}
                    label="Import Mode"
                  >
                    <MenuItem value="skip">Skip Invalid Rows</MenuItem>
                    <MenuItem value="strict">Strict</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <input
                  accept=".csv,text/csv"
                  id="dcl-csv-file-input"
                  type="file"
                  style={{ display: "none" }}
                  onChange={handleCsvFileChange}
                />
                <Box display="flex" alignItems="center" gridGap={12} flexWrap="wrap">
                  <label htmlFor="dcl-csv-file-input">
                    <Button variant="outlined" color="primary" component="span">
                      Choose CSV File
                    </Button>
                  </label>
                  <Typography variant="body2" className={classes.subtleText}>
                    {csvFileName || "No file selected"}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            {csvHeaders.length > 0 && (
              <Box mt={2}>
                <Typography variant="subtitle2" style={{ marginBottom: 12 }}>
                  Column Mapping
                </Typography>
                <Grid container spacing={2}>
                  {csvMappingFields.map((field) => (
                    <Grid item xs={12} sm={6} md={4} key={field}>
                      <FormControl fullWidth variant="outlined" size="small">
                        <InputLabel>{titleCase(field)}</InputLabel>
                        <Select
                          value={csvMapping[field] || ""}
                          onChange={handleCsvMappingChange(field)}
                          label={titleCase(field)}
                        >
                          <MenuItem value="">
                            <em>Ignore</em>
                          </MenuItem>
                          {csvHeaders.map((header) => (
                            <MenuItem key={`${field}-${header}`} value={header}>
                              {header}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
            <Box className={classes.actionsRow}>
              <Button
                color="primary"
                variant="contained"
                onClick={handleImportCsv}
                disabled={!csvText}
              >
                Import DCL CSV
              </Button>
            </Box>
          </Paper>
        </Box>
      )}

      {tab === 4 && (
        <Box mt={3}>
          <Paper className={classes.paper}>
            <Typography className={classes.sectionTitle}>
              DCL Events
            </Typography>
            <Box display="flex" justifyContent="flex-end" mb={2}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={exportEventsCsv}
                style={{ marginRight: 8 }}
              >
                Download CSV
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => withLoading(loadEvents)}
                className={classes.refreshButton}
              >
                Refresh Events
              </Button>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Box</InputLabel>
                  <Select
                    value={eventFilters.box}
                    onChange={(e) => {
                      setEventPage(1);
                      setEventFilters((prev) => ({
                        ...prev,
                        box: e.target.value,
                        subPort: "",
                      }));
                    }}
                    label="Box"
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {simBoxesUnifiedSet.map((box) => (
                      <MenuItem key={box} value={box}>
                        {box}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>SubPort</InputLabel>
                  <Select
                    value={eventFilters.subPort}
                    onChange={(e) => {
                      setEventPage(1);
                      setEventFilters((prev) => ({ ...prev, subPort: e.target.value }));
                    }}
                    label="SubPort"
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {eventFilterAllowedSubPorts.map((sp) => (
                      <MenuItem key={sp} value={String(sp)}>
                        {sp}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Port"
                  value={eventFilters.port}
                  onChange={(e) => {
                    setEventPage(1);
                    setEventFilters((prev) => ({ ...prev, port: e.target.value }));
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Carrier</InputLabel>
                  <Select
                    value={eventFilters.carrier}
                    onChange={(e) => {
                      setEventPage(1);
                      setEventFilters((prev) => ({ ...prev, carrier: e.target.value }));
                    }}
                    label="Carrier"
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {filterCarrierOptions.map((carrier) => (
                      <MenuItem key={carrier.value} value={carrier.value}>
                        {carrier.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Action</InputLabel>
                  <Select
                    value={eventFilters.action}
                    onChange={(e) => {
                      setEventPage(1);
                      setEventFilters((prev) => ({ ...prev, action: e.target.value }));
                    }}
                    label="Action"
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    <MenuItem value="skip">Skip</MenuItem>
                    <MenuItem value="reroute">Reroute</MenuItem>
                    <MenuItem value="backwash">Backwash</MenuItem>
                    <MenuItem value="dontSend">DontSend</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            {eventTotal > 0 && (
              <Pagination
                pages={eventPages}
                currentPage={eventPage}
                handlePage={setEventPage}
                currentLimit={eventLimit}
                handleLimit={(value) => {
                  setEventPage(1);
                  setEventLimit(Number(value));
                }}
                totalCounts={eventTotal}
                showHead="true"
              />
            )}
            <Box mt={2} className={classes.tableWrap}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {["When", "Scope", "Box", "SubPort", "Port", "Carrier", "Action", "Mode", "Limit", "Reason", "Phone", "Reroute"].map(
                      (label) => (
                        <TableCell key={label} className={classes.tableHeadCell}>
                          {label}
                        </TableCell>
                      )
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {events.length ? (
                    events.map((event) => (
                      <TableRow
                        key={`${event._id}`}
                        className={classes.tableRow}
                      >
                        <TableCell>{event.createdAt ? new Date(event.createdAt).toLocaleString() : "-"}</TableCell>
                        <TableCell>{humanizeToken(event.scope)}</TableCell>
                        <TableCell>{event.box || "-"}</TableCell>
                        <TableCell>{event.subPort || "-"}</TableCell>
                        <TableCell>{event.port || "-"}</TableCell>
                        <TableCell>{humanizeToken(event.carrier)}</TableCell>
                        <TableCell>{humanizeToken(event.action)}</TableCell>
                        <TableCell>{humanizeToken(event.mode)}</TableCell>
                        <TableCell>{event.limit ?? "-"}</TableCell>
                        <TableCell>{event.reason || "-"}</TableCell>
                        <TableCell>{event.phone || "-"}</TableCell>
                        <TableCell>{event.reroutePhone || "-"}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={12} align="center" className={classes.emptyCell}>
                        No DCL events found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
            {eventTotal > 0 && (
              <Pagination
                pages={eventPages}
                currentPage={eventPage}
                handlePage={setEventPage}
                currentLimit={eventLimit}
                handleLimit={(value) => {
                  setEventPage(1);
                  setEventLimit(Number(value));
                }}
                totalCounts={eventTotal}
                showHead="false"
              />
            )}
          </Paper>
        </Box>
      )}

      {tab === 5 && (
        <Box mt={3}>
          <Paper className={classes.paper}>
            <Typography className={classes.sectionTitle}>
              DCL Summary
            </Typography>
            <Box display="flex" justifyContent="flex-end" mb={2}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={exportSummaryCsv}
                style={{ marginRight: 8 }}
              >
                Download CSV
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => withLoading(loadSummary)}
                className={classes.refreshButton}
              >
                Refresh Summary
              </Button>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Box</InputLabel>
                  <Select
                    value={summaryFilters.box}
                    onChange={(e) => {
                      setSummaryPage(1);
                      setSummaryFilters((prev) => ({
                        ...prev,
                        box: e.target.value,
                        subPort: "",
                      }));
                    }}
                    label="Box"
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {simBoxesUnifiedSet.map((box) => (
                      <MenuItem key={box} value={box}>
                        {box}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>SubPort</InputLabel>
                  <Select
                    value={summaryFilters.subPort}
                    onChange={(e) => {
                      setSummaryPage(1);
                      setSummaryFilters((prev) => ({ ...prev, subPort: e.target.value }));
                    }}
                    label="SubPort"
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {summaryFilterAllowedSubPorts.map((sp) => (
                      <MenuItem key={sp} value={String(sp)}>
                        {sp}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Port"
                  value={summaryFilters.port}
                  onChange={(e) => {
                    setSummaryPage(1);
                    setSummaryFilters((prev) => ({ ...prev, port: e.target.value }));
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Carrier</InputLabel>
                  <Select
                    value={summaryFilters.carrier}
                    onChange={(e) => {
                      setSummaryPage(1);
                      setSummaryFilters((prev) => ({ ...prev, carrier: e.target.value }));
                    }}
                    label="Carrier"
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {filterCarrierOptions.map((carrier) => (
                      <MenuItem key={carrier.value} value={carrier.value}>
                        {carrier.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Action</InputLabel>
                  <Select
                    value={summaryFilters.action}
                    onChange={(e) => {
                      setSummaryPage(1);
                      setSummaryFilters((prev) => ({ ...prev, action: e.target.value }));
                    }}
                    label="Action"
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    <MenuItem value="skip">Skip</MenuItem>
                    <MenuItem value="reroute">Reroute</MenuItem>
                    <MenuItem value="backwash">Backwash</MenuItem>
                    <MenuItem value="dontSend">DontSend</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            {summaryTotal > 0 && (
              <Pagination
                pages={summaryPages}
                currentPage={summaryPage}
                handlePage={setSummaryPage}
                currentLimit={summaryLimit}
                handleLimit={(value) => {
                  setSummaryPage(1);
                  setSummaryLimit(Number(value));
                }}
                totalCounts={summaryTotal}
                showHead="true"
              />
            )}
            <Box mt={2} className={classes.tableWrap}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {["Box", "SubPort", "Port", "Carrier", "Action", "Count"].map((label) => (
                      <TableCell key={label} className={classes.tableHeadCell}>
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summary.length ? (
                    summary.map((item, idx) => (
                      <TableRow
                        key={`${item?._id?.box || "x"}-${item?._id?.subPort || "x"}-${item?._id?.port || "x"}-${item?._id?.carrier || "x"}-${idx}`}
                        className={classes.tableRow}
                      >
                        <TableCell>{item?._id?.box || "-"}</TableCell>
                        <TableCell>{item?._id?.subPort || "-"}</TableCell>
                        <TableCell>{item?._id?.port || "-"}</TableCell>
                        <TableCell>{humanizeToken(item?._id?.carrier)}</TableCell>
                        <TableCell>{humanizeToken(item?._id?.action)}</TableCell>
                        <TableCell>{item?.count ?? 0}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center" className={classes.emptyCell}>
                        No DCL summary rows found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
            {summaryTotal > 0 && (
              <Pagination
                pages={summaryPages}
                currentPage={summaryPage}
                handlePage={setSummaryPage}
                currentLimit={summaryLimit}
                handleLimit={(value) => {
                  setSummaryPage(1);
                  setSummaryLimit(Number(value));
                }}
                totalCounts={summaryTotal}
                showHead="false"
              />
            )}
          </Paper>
        </Box>
      )}

      <ConfirmDialog
        open={confirmState.open}
        title={confirmState.title}
        content={confirmState.content}
        onCancel={closeConfirm}
        onConfirm={async () => {
          if (typeof confirmState.onConfirm === "function") {
            await confirmState.onConfirm();
          }
        }}
      />
      <Dialog
        open={Boolean(usageDialogConfig)}
        onClose={closeUsageDialog}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          <Box className={classes.usageDialogTitleBar}>
            <Box className={`${classes.usageDialogHeader} ${classes.usageDialogTitleWrap}`}>
              <Typography className={classes.usageDialogKicker}>
                Daily Carrier Limit
              </Typography>
              <Typography variant="h5" className={classes.usageDialogTitle}>
                Per-Port Usage
                {usageDialogConfig?.box
                  ? ` - Box ${usageDialogConfig.box}${
                      usageDialogConfig.scope === "subport" || usageDialogConfig.scope === "port"
                        ? ` / SubPort ${usageDialogConfig.subPort}`
                        : ""
                    }`
                  : ""}
              </Typography>
              <Typography className={classes.usageDialogSubtitle}>
                Carrier quota view for the currently selected target.
              </Typography>
            </Box>
            <IconButton
              aria-label="Close per-port usage dialog"
              className={classes.usageDialogCloseButton}
              onClick={closeUsageDialog}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {usageDialogConfig &&
          renderUsageSummary(usageDialogConfig).some(
            (entry) => entry.countMode === "per_port"
          ) ? (
            <>
              {(() => {
                const entries = renderUsageSummary(usageDialogConfig).filter(
                  (entry) => entry.countMode === "per_port"
                );
                const overview = entries.reduce(
                  (acc, entry) => {
                    const summary = summarizeCarrierPortUsage(entry);
                    acc.carriers += 1;
                    acc.tracked += summary.trackedPortCount;
                    acc.active += summary.activeCount;
                    acc.exhausted += summary.exhaustedCount;
                    return acc;
                  },
                  { carriers: 0, tracked: 0, active: 0, exhausted: 0 }
                );

                return (
                  <>
                    <Box className={classes.usageDialogOverview}>
                      <Box className={classes.usageDialogOverviewCard}>
                        <Typography className={classes.usageDialogOverviewLabel}>
                          Carriers
                        </Typography>
                        <Typography className={classes.usageDialogOverviewValue}>
                          {overview.carriers}
                        </Typography>
                        <Typography className={classes.usageDialogOverviewHint}>
                          carriers tracked in this rule
                        </Typography>
                      </Box>
                      <Box className={classes.usageDialogOverviewCard}>
                        <Typography className={classes.usageDialogOverviewLabel}>
                          Live Ports
                        </Typography>
                        <Typography className={classes.usageDialogOverviewValue}>
                          {overview.tracked}
                        </Typography>
                        <Typography className={classes.usageDialogOverviewHint}>
                          carrier-port quotas visible in this view
                        </Typography>
                      </Box>
                      <Box className={classes.usageDialogOverviewCard}>
                        <Typography className={classes.usageDialogOverviewLabel}>
                          Available
                        </Typography>
                        <Typography className={classes.usageDialogOverviewValue}>
                          {overview.active}
                        </Typography>
                        <Typography className={classes.usageDialogOverviewHint}>
                          quotas with remaining capacity
                        </Typography>
                      </Box>
                      <Box className={classes.usageDialogOverviewCard}>
                        <Typography className={classes.usageDialogOverviewLabel}>
                          Exhausted
                        </Typography>
                        <Typography className={classes.usageDialogOverviewValue}>
                          {overview.exhausted}
                        </Typography>
                        <Typography className={classes.usageDialogOverviewHint}>
                          quotas that cannot send more today
                        </Typography>
                      </Box>
                    </Box>
                  </>
                );
              })()}
              {(() => {
                const entries = renderUsageSummary(usageDialogConfig).filter(
                  (entry) => entry.countMode === "per_port"
                );
                const matrix = buildUsageMatrix(usageDialogConfig, entries);
                const preferredSubPort =
                  String(
                    matrix.find((section) =>
                      section.ports.some((portRow) =>
                        portRow.carrierStatuses.some(
                          (status) => String(status.status) !== "missing"
                        )
                      )
                    )?.subPort ||
                      matrix[0]?.subPort ||
                      "1"
                  );
                const activeSubPort =
                  usageDialogSubPortTab ||
                  preferredSubPort;
                const visibleSections =
                  usageDialogConfig.scope === "box"
                    ? matrix.filter(
                        (section) => String(section.subPort) === String(activeSubPort)
                      )
                    : matrix;

                return (
                  <>
                    {usageDialogConfig.scope === "box" && matrix.length > 1 && (
                      <Box className={classes.usageMatrixTabsWrap}>
                        <Tabs
                          className={classes.usageMatrixTabs}
                          value={activeSubPort}
                          onChange={(_, value) => setUsageDialogSubPortTab(String(value))}
                          indicatorColor="primary"
                          textColor="primary"
                          variant="fullWidth"
                        >
                          {matrix.map((section) => (
                            <Tab
                              className={classes.usageMatrixTab}
                              key={`subport-tab-${section.subPort}`}
                              value={String(section.subPort)}
                              label={`SubPort ${section.subPort}`}
                            />
                          ))}
                        </Tabs>
                      </Box>
                    )}
                    {visibleSections.map((section) => (
                  <Box
                    key={`matrix-${section.subPort}`}
                    className={classes.usageMatrixSection}
                  >
                    {usageDialogConfig.scope !== "box" && (
                      <Typography className={classes.usageMatrixSectionTitle}>
                        SubPort {section.subPort}
                      </Typography>
                    )}
                    <Box className={classes.usageMatrixWrap}>
                      <Table stickyHeader className={classes.usageMatrixTable}>
                        <TableHead>
                          <TableRow>
                            {usageDialogConfig.scope !== "box" && (
                              <TableCell className={classes.usageMatrixHeadCell}>
                                SubPort
                              </TableCell>
                            )}
                            <TableCell className={classes.usageMatrixHeadCell}>
                              Port
                            </TableCell>
                            {entries.map((entry) => (
                              <TableCell
                                key={`head-${section.subPort}-${entry.carrier}`}
                                className={classes.usageMatrixHeadCell}
                              >
                                {entry.carrier}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {section.ports.map((portRow, idx) => (
                            <TableRow key={`row-${section.subPort}-${portRow.port}`}>
                              {usageDialogConfig.scope !== "box" && (
                                <TableCell
                                  className={
                                    idx % 2 === 0
                                      ? classes.usageMatrixStickyCell
                                      : classes.usageMatrixStickyCellAlt
                                  }
                                >
                                  {section.subPort}
                                </TableCell>
                              )}
                              <TableCell className={classes.usageMatrixPortCell}>
                                P{portRow.port}
                              </TableCell>
                              {portRow.carrierStatuses.map((status) => (
                                <TableCell
                                  key={`cell-${section.subPort}-${portRow.port}-${status.carrier}`}
                                  className={classes.usageMatrixStatusCell}
                                >
                                  <Box
                                    className={`${classes.usageMatrixStatusBox} ${
                                      status.status === "available"
                                        ? classes.usageMatrixStatusAvailable
                                        : status.status === "low"
                                        ? classes.usageMatrixStatusLow
                                        : status.status === "exhausted"
                                        ? classes.usageMatrixStatusExhausted
                                        : status.status === "unused"
                                        ? classes.usageMatrixStatusUnused
                                        : classes.usageMatrixStatusMissing
                                    }`}
                                  >
                                    <Typography className={classes.usageMatrixStatusPrimary}>
                                      {status.primary}
                                    </Typography>
                                    <Typography className={classes.usageMatrixStatusSecondary}>
                                      {status.secondary}
                                    </Typography>
                                  </Box>
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  </Box>
                    ))}
                  </>
                );
              })()}
            </>
          ) : (
            <Typography className={classes.subtleText}>
              No per-port usage available.
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default DclManager;
